import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { type MediaStudioMeta } from '../media-studio/MediaStudioDialog';

import { VideoStudioDialog, type VideoStudioDialogContext } from './VideoStudioDialog';

import { useAuth } from '@/hooks/use-auth';
import { useManualPostDraft } from '@/hooks/useManualPostDraft';
import { MODEL_DISPLAY_NAMES, VIDEO_MODELS } from '@/lib/ai-agent/model-constants';
import { campaignService } from '@/services/data/CampaignService';
import { postService } from '@/services/data/PostService';
import { videoJobService } from '@/services/data/VideoJobService';
import { useVideoDialogStore } from '@/stores/useVideoDialogStore';
import { downloadBlob } from '@/utils/downloadBlob';
import { logger } from '@/utils/logger';

function formatDuration(d: number | undefined): string {
  if (!d) return '—';
  return `${d}s`;
}

function seedToString(seed: string | number | undefined): string {
  if (seed === undefined || seed === null || seed === '') return '—';
  return String(seed);
}

function deriveTitle(prompt: string | undefined): string {
  if (!prompt) return 'Untitled video';
  const cleaned = prompt.trim().replace(/\s+/g, ' ');
  if (cleaned.length <= 80) return cleaned;
  return `${cleaned.slice(0, 77)}...`;
}

function isFastModel(model: string | undefined): boolean {
  if (!model) return true;
  const normalized = model.toLowerCase();
  if (normalized.includes('fast')) return true;
  if (normalized.includes('pro')) return false;
  return true;
}

interface VideoStudioDialogMountProps {
  inline?: boolean;
  breadcrumb?: { rootLabel: string; rootHref?: string | undefined; leafLabel: string } | undefined;
}

export function VideoStudioDialogMount({
  inline = false,
  breadcrumb,
}: VideoStudioDialogMountProps = {}) {
  const isOpen = useVideoDialogStore((s) => s.isOpen);
  const payload = useVideoDialogStore((s) => s.payload);
  const close = useVideoDialogStore((s) => s.close);
  const { session } = useAuth();
  const navigate = useNavigate();
  const { createManualPostDraft, isCreatingManualPost } = useManualPostDraft();

  const getAuthHeader = useCallback((): string | null => {
    const token = session?.access_token;
    if (!token) return null;
    return `Bearer ${token}`;
  }, [session?.access_token]);

  const postGenerateVideo = useCallback(
    async (body: Record<string, unknown>): Promise<{ jobId: string } | null> => {
      const auth = getAuthHeader();
      if (!auth) {
        toast.error('Not signed in — please refresh.');
        return null;
      }
      try {
        const resp = await fetch('/api/generate-video', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: auth,
            'Idempotency-Key': crypto.randomUUID(),
          },
          body: JSON.stringify(body),
        });
        if (!resp.ok) {
          const text = await resp.text().catch(() => '');
          toast.error(`Video request failed (${resp.status}) ${text}`.trim());
          return null;
        }
        const json = (await resp.json()) as { jobId?: string };
        if (!json.jobId) {
          toast.error('Video API returned no jobId');
          return null;
        }
        return { jobId: json.jobId };
      } catch (err) {
        toast.error(`Video request error: ${err instanceof Error ? err.message : 'unknown error'}`);
        return null;
      }
    },
    [getAuthHeader],
  );

  const handleVariations = useCallback(async () => {
    if (!payload) return;
    const job = await videoJobService.getVideoJobById(payload.jobId);
    if (!job || !job.prompt) {
      toast.error('Original prompt not found — cannot create variations.');
      return;
    }
    const duration =
      job.duration_seconds === 4 || job.duration_seconds === 6 ? job.duration_seconds : 8;
    const aspect = (payload.aspectRatio ?? '9:16') as '9:16' | '1:1' | '4:5' | '16:9';
    toast.message('Generating 3 variations — they will appear in your library.');
    await Promise.all(
      [0, 1, 2].map(() =>
        postGenerateVideo({
          prompt: job.prompt,
          duration,
          aspectRatio: aspect,
          resolution: job.resolution ?? '720p',
          model: job.model ?? VIDEO_MODELS.VEO_31_FAST,
          audio: true,
          seed: Math.floor(Math.random() * 1_000_000_000),
          ...(job.source_image_url ? { sourceImageUrl: job.source_image_url } : {}),
        }),
      ),
    );
  }, [payload, postGenerateVideo]);

  const handleExtend = useCallback(() => {
    toast.message(
      'Extending videos is coming in v2. For now, Regenerate with a slightly different prompt for a sibling clip.',
    );
  }, []);

  // Mirrors ImageBody.tsx's handleCreatePost: creates an unscheduled draft
  // and opens the real Post Inspector editor (platform, caption, date all
  // visible/settable before anything is scheduled or posted) instead of the
  // old "Approve & schedule" path, which blind-created a placeholder post
  // and auto-scheduled it to the next open calendar slot with zero review.
  const handleCreatePost = useCallback(() => {
    if (!payload?.videoUrl || isCreatingManualPost) return;
    void createManualPostDraft({
      mediaUrls: [payload.videoUrl],
      title: payload.title || deriveTitle(payload.prompt),
      mediaType: 'video',
    })
      .then(() => close())
      .catch(() => {
        // useManualPostDraft shows the recovery toast. Keep the studio open
        // so the user can retry instead of losing context after a failure.
      });
  }, [payload, isCreatingManualPost, createManualPostDraft, close]);

  const handleSaveToPlan = useCallback(async () => {
    if (!payload) return;
    try {
      const postResp = await postService.getByVideoJobId(payload.jobId);
      const post = postResp.data ?? null;
      if (post?.campaign_id) {
        const planResp = await campaignService.getCampaign(post.campaign_id);
        const planName = planResp.data?.name;
        toast.message(`Already in project${planName ? ` "${planName}"` : ''}.`);
        return;
      }
      if (post?.id) {
        navigate(`/projects?assign=${encodeURIComponent(post.id)}`);
        return;
      }
      toast.error('No draft post found for this video yet — try again in a few seconds.');
    } catch (err) {
      toast.error(`Add to project failed: ${err instanceof Error ? err.message : 'unknown error'}`);
    }
  }, [payload, navigate]);

  if (!payload) return null;

  const model = payload.model || MODEL_DISPLAY_NAMES[VIDEO_MODELS.VEO_31];
  const aspect = payload.aspectRatio || '9:16';
  const meta: MediaStudioMeta = {
    model,
    duration: formatDuration(payload.duration),
    aspect,
    seed: seedToString(payload.seed),
  };

  const context: VideoStudioDialogContext = {
    jobId: payload.jobId,
    videoUrl: payload.videoUrl,
    prompt: payload.prompt,
    model,
    duration: payload.duration,
    aspectRatio: aspect,
  };

  const handleDownload = () => {
    if (!payload.videoUrl) {
      toast.error('Video is still processing — download will be available once ready.');
      return;
    }
    void downloadBlob(payload.videoUrl, `mora-video-${payload.jobId}.mp4`);
  };

  return (
    <VideoStudioDialog
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      videoUrl={payload.videoUrl}
      posterUrl={payload.posterUrl ?? undefined}
      title={payload.title || deriveTitle(payload.prompt)}
      {...(breadcrumb ? { breadcrumb } : {})}
      meta={meta}
      inline={inline}
      thumbnailUrl={payload.thumbnailUrl ?? payload.posterUrl ?? undefined}
      isFastTier={isFastModel(payload.model)}
      context={context}
      onDownload={handleDownload}
      onVariations={handleVariations}
      onSaveToPlan={handleSaveToPlan}
      artifactId={payload.jobId}
      artifactKind="media"
      onCreatePost={handleCreatePost}
      isCreatingPost={isCreatingManualPost}
      onChipAction={async (chip) => {
        if (chip.namedAction === 'extend') {
          handleExtend();
          return;
        }
        if (chip.namedAction === 'caption') {
          if (!payload.videoUrl) {
            toast.error('Video still rendering — caption available once ready.');
            return;
          }
          const params = new URLSearchParams({
            prefill: `Write an Instagram caption for this video: ${payload.videoUrl}`,
            platforms: 'instagram',
          });
          navigate(`/?${params.toString()}`);
          close();
          return;
        }
        if (chip.namedAction === 'orbit') {
          if (!payload) return;
          const job = await videoJobService.getVideoJobById(payload.jobId);
          if (!job?.prompt) {
            toast.error('Original prompt missing — cannot apply camera move.');
            return;
          }
          const dur =
            job.duration_seconds === 4 || job.duration_seconds === 6 ? job.duration_seconds : 8;
          const result = await postGenerateVideo({
            prompt: `${job.prompt} — Camera slowly orbits the subject, keeping distance consistent.`,
            duration: dur,
            aspectRatio: (payload.aspectRatio ?? '9:16') as '9:16' | '1:1' | '4:5' | '16:9',
            resolution: job.resolution ?? '720p',
            model: job.model ?? VIDEO_MODELS.VEO_31_FAST,
            audio: true,
            ...(job.source_image_url ? { sourceImageUrl: job.source_image_url } : {}),
          });
          if (result) {
            toast.message('Re-rendering with orbit camera — ~90 s.');
          }
          return;
        }
        if (chip.action === 'auto-send' && chip.prompt) {
          if (!payload) return;
          const job = await videoJobService.getVideoJobById(payload.jobId);
          if (!job?.prompt) {
            toast.error('Original prompt missing — cannot regenerate.');
            return;
          }
          const dur =
            job.duration_seconds === 4 || job.duration_seconds === 6 ? job.duration_seconds : 8;
          const result = await postGenerateVideo({
            prompt: `${job.prompt} — ${chip.prompt}`,
            duration: dur,
            aspectRatio: (payload.aspectRatio ?? '9:16') as '9:16' | '1:1' | '4:5' | '16:9',
            resolution: job.resolution ?? '720p',
            model: job.model ?? VIDEO_MODELS.VEO_31_FAST,
            audio: true,
            ...(chip.id === 'slow-motion' ? { fps: '60' as const } : {}),
            ...(job.source_image_url ? { sourceImageUrl: job.source_image_url } : {}),
          });
          if (result) {
            toast.message(`${chip.label} — re-rendering, ~90 s.`);
          }
          return;
        }
        // After the 2026-05-12 chip subtraction, every chip in ICP_CHIPS.video
        // has a real handler above. If we land here, a new chip was added
        // without a matching branch — surface as engineering error, not a
        // user-facing "coming soon" toast.
        logger.error('[MediaStudio] Unhandled video chip action', chip);
      }}
    />
  );
}
